/* towsons-replay.js — SESSION RECORDINGS: what a visitor saw and did, recorded so the shop's people can watch it
 * back in the portal (Storefront → Analytics → Visits; an order's Journey tab). Philip, 5 Oct 2026: "I'd like
 * heatmaps and session replays of visitor sessions" — "as efficient as possible", "accepted like all the other
 * paid for heatmap/session recording services", "filtering out all bots is important".
 *
 * HOW IT IS ACCEPTED. Nothing is recorded until the visitor agrees to analytics (Shopify's own customer-privacy
 * consent — the same gate as the shop's analytics script), and never when the browser asks not to be tracked
 * (Global Privacy Control). What a visitor who agreed types into the search boxes and the personaliser, and what the
 * personaliser, the discover module and a basket line show of it, is recorded as it is (Philip, 28 Oct 2026: "we
 * don't actually need to censor what the user types into search or product customisation boxes … if they agree to
 * cookies we can record everything possible that will help us"); every other box's value is masked (****), and an
 * email, phone, password or card box always is, as is anything in the sign-in, contact and newsletter forms (TYPED_OK
 * and PRIVATE_BOX below). The personaliser's preview is a canvas, which is not captured. The shop's own email-choices
 * and review pages' parts are blocked out whole (BLOCK below — a box of the same size in the player, nothing of what
 * was in it), as is anything the theme marks data-tw-block; nothing is recorded on account, order or password pages,
 * nothing at all on the email-choices and review pages (the snippet does not load the recorder there), and Shopify's
 * checkout cannot be. Philip, 6 Oct 2026, of everything between the shop and the portal: "make sure it's secure and
 * solid". The portal keeps a recording 30 days unless the visit led to an order, when it is kept with the order.
 *
 * HOW IT IS CHEAP. rrweb (assets/towsons-rrweb-record.min.js, MIT) records the page once as it is and then only
 * what changes; mouse movement is thinned, scrolling and typing are sampled, pictures and fonts are not copied
 * (the player loads them from the shop's own CDN); each batch is gzipped in the browser before it goes
 * (CompressionStream) and sent as a plain-text post (no preflight) to the shop's own data address
 * (data.<its domain>/v1/replay — the shop-edge Worker, which keeps it and refuses bots and visitors from outside
 * the shop's countries). A recording stops after twenty minutes on a page or two megabytes sent.
 *
 * ONLY PEOPLE (Philip, 7 Oct 2026: "a product page is landed on, scrolls to the bottom and then a new product page is
 * loaded … it feels/looks like bot activity" — 61% of recordings were: a desktop that never moved its mouse, scrolled
 * in one or two jumps, went to a product the page never linked). Nothing is sent until the page has had something only
 * a person does — a mouse that moves, a touch, a key, a wheel — and the event is the browser's own (isTrusted), not a
 * script's. Until then the recording waits in memory; a page left without one is never sent at all. A browser that
 * says it is automated (navigator.webdriver) is never recorded.
 *
 * WHO. The visitor is Shopify's own id for the browser (the one its pixel and the analytics script use), so a
 * recording and the visit's events are one visitor. Which visitors are recorded is the portal's choice (the shop's
 * config, towsons.config.replay: on, and one in `sample` visitors — by a hash of the id, so a visitor is either
 * always recorded or never).
 */
(function () {
  'use strict';
  if (window.__twReplay) return;
  window.__twReplay = true;
  var me = document.currentScript || document.querySelector('script[data-tw-replay]');
  if (!me) return;
  var ENDPOINT = String(me.getAttribute('data-endpoint') || '').replace(/\/+$/, '');
  var SAMPLE = Math.max(1, parseInt(me.getAttribute('data-sample'), 10) || 1);
  var PAGE_TYPE = String(me.getAttribute('data-page') || '');
  var TEMPLATE = String(me.getAttribute('data-template') || '');
  var VERSION = parseInt(me.getAttribute('data-version'), 10) || 0;
  if (!/^https:\/\/data\./.test(ENDPOINT)) return;
  if (!window.rrwebRecord || typeof window.rrwebRecord.record !== 'function' && typeof window.rrwebRecord !== 'function') return;

  var FLUSH_MS = 8000;
  var FLUSH_CHARS = 48000;       // a batch is sent when its JSON reaches this (~8–12 KB gzipped)
  var KEEPALIVE_MAX = 60000;     // a keepalive post may carry at most 64 KB
  var MAX_PAGE_MS = 20 * 60000;
  var MAX_PAGE_BYTES = 2 * 1024 * 1024;
  var MASK_TEXT = '[data-tw-mask], .rr-mask';
  // Blocked out whole: anything the theme marks, and the email-choices and review pages' roots (towsons-email-prefs,
  // towsons-review-form: a person's own choices, a reviewer's own words).
  var BLOCK = '[data-tw-block], [data-tep], [data-trf], .rr-block';
  // The boxes whose words are recorded as typed (the same as the analytics script's TYPED): the personaliser's, a
  // product form's own custom boxes, the discover module's name box, the search boxes. Every other box is masked —
  // and these too when PRIVATE_BOX holds.
  var TYPED_OK = '.towsons-personaliser__input, .towsons-personaliser__line-input, [data-tdc-name], [data-help-search-input], input[type="search"], .search-bar__input, input[name="q"], [data-tw-suggest], form[action*="/cart/add"] input[name^="properties["], form[action*="/cart/add"] textarea[name^="properties["]';
  var PRIVATE_BOX = 'input[type="password"], input[type="email"], input[type="tel"], [autocomplete~="email"], [autocomplete~="tel"], [autocomplete^="cc-"], form[action*="/account"] *, form[action*="/contact"] *, form[action*="/customer"] *, .newsletter *, [data-pa-private], [data-pa-private] *, [data-tw-mask], [data-tw-mask] *';
  function boxShown(el) {
    try { return !!(el && el.matches && el.matches(TYPED_OK) && !el.matches(PRIVATE_BOX)); } catch (e) { return false; }
  }
  // rrweb hands every box's value here (maskAllInputs): a box above as it is, any other as stars.
  function maskBox(text, el) { return boxShown(el) ? text : String(text || '').replace(/[\S\s]/g, '*'); }

  function shop() { return window.Shopify || {}; }
  if (shop().designMode) return;                                            // not in the theme editor
  if (/^customers\/|^password$|^gift_card$/.test(PAGE_TYPE)) return;        // nobody's account, ever
  try { if (navigator.webdriver === true) return; } catch (e) { /* no signal: fine */ }   // an automated browser
  try { if (navigator.globalPrivacyControl) return; } catch (e) { /* no signal: fine */ }

  // ── consent: Shopify's own, as the analytics script reads it ──
  function allowed() {
    try {
      var cp = shop().customerPrivacy;
      return !!(cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed());
    } catch (e) { return false; }
  }
  var started = false;
  function tryStart() { if (!started && allowed()) { started = true; start(); } }
  document.addEventListener('visitorConsentCollected', function () { setTimeout(tryStart, 0); });
  function boot() {
    if (shop().customerPrivacy) return tryStart();
    var S = shop();
    if (typeof S.loadFeatures === 'function') {
      try { S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) { if (!err) tryStart(); }); } catch (e) { /* no consent API: nothing recorded */ }
    }
  }

  // ── who ──
  function rid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
  function cookie(n) { var m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : null; }
  function clientId() {
    try {
      var A = window.ShopifyAnalytics;
      var t = A && A.lib && typeof A.lib.user === 'function' && A.lib.user().traits().uniqToken;
      if (t) return String(t);
    } catch (e) { /* below */ }
    var y = cookie('_shopify_y');
    if (y) return y;
    try { var own = localStorage.getItem('tw_pa_client'); if (!own) { own = 'pa-' + rid(); localStorage.setItem('tw_pa_client', own); } return own; } catch (e) { return 'pa-' + rid(); }
  }
  // One in SAMPLE visitors, always the same ones (FNV-1a of the id, as the Worker samples).
  function chosen(id) {
    if (SAMPLE <= 1) return true;
    var h = 0x811c9dc5;
    for (var i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h % SAMPLE === 0;
  }

  // ── sending ──
  var RID = rid();
  var CLIENT = null;
  var seq = 0;
  var sent = 0;
  var buf = [];
  var bufChars = 0;
  var timer = null;
  var stopped = false;
  var stopRecording = null;
  var T0 = Date.now();
  // A person yet? Until then nothing is sent, and a page that waits too long for one is not recorded.
  var human = false;
  var WAIT_CHARS = 1500000;
  var HUMAN_EVENTS = ['pointermove', 'mousemove', 'pointerdown', 'touchstart', 'keydown', 'wheel'];
  function onHuman(e) {
    if (human || !e || e.isTrusted === false) return;
    if ((e.type === 'mousemove' || e.type === 'pointermove') && !e.movementX && !e.movementY) return;
    human = true;
    HUMAN_EVENTS.forEach(function (t) { window.removeEventListener(t, onHuman, true); });
    if (buf.length && !stopped) flushAll();
  }
  HUMAN_EVENTS.forEach(function (t) { window.addEventListener(t, onHuman, { capture: true, passive: true }); });

  function url() {
    return ENDPOINT + '/v1/replay?rid=' + encodeURIComponent(RID) + '&seq=' + seq + '&c=' + encodeURIComponent(CLIENT || '') + '&p=' + encodeURIComponent(location.pathname.slice(0, 255))
      + '&t=' + T0 + '&v=' + VERSION + '&tp=' + encodeURIComponent(TEMPLATE.slice(0, 40));
  }
  function post(body, enc, unloading) {
    var size = body.byteLength != null ? body.byteLength : body.size != null ? body.size : String(body).length;
    sent += size;
    var u = url() + (enc ? '&enc=' + enc : '');
    seq += 1;
    try {
      fetch(u, { method: 'POST', body: body, keepalive: !!unloading && size <= KEEPALIVE_MAX, credentials: 'omit', headers: { 'Content-Type': 'text/plain' } }).catch(function () { /* lost: the shop is not slowed */ });
    } catch (e) { /* no fetch: nothing sent */ }
    if (sent >= MAX_PAGE_BYTES) stop();
  }
  function flush(unloading) {
    if (!buf.length || !human) return;
    var json = '[' + buf.join(',') + ']';
    buf = []; bufChars = 0;
    var canZip = typeof CompressionStream === 'function' && typeof Blob === 'function' && !unloading;
    if (!canZip) { post(json, '', unloading); return; }
    try {
      var stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
      new Response(stream).arrayBuffer().then(function (bytes) { post(bytes, 'gzip', false); }).catch(function () { post(json, '', false); });
    } catch (e) { post(json, '', false); }
  }
  // What waited for a person, sent once one is there — in batches of the usual size, so none is too big to keep.
  function flushAll() {
    var all = buf;
    buf = []; bufChars = 0;
    var part = [], chars = 0;
    for (var i = 0; i < all.length; i++) {
      part.push(all[i]); chars += all[i].length;
      if (chars >= FLUSH_CHARS * 4 || i === all.length - 1) { buf = part; bufChars = chars; flush(false); part = []; chars = 0; }
    }
  }
  function schedule() {
    if (timer) return;
    timer = setTimeout(function () { timer = null; flush(false); }, FLUSH_MS);
  }
  function emit(ev) {
    if (stopped) return;
    var s;
    try { s = JSON.stringify(ev); } catch (e) { return; }
    buf.push(s);
    bufChars += s.length;
    if (!human) {                                                 // waiting for a person: kept, not sent
      if (bufChars > WAIT_CHARS) { stopped = true; buf = []; bufChars = 0; try { if (stopRecording) stopRecording(); } catch (e) { /* done */ } }
      return;
    }
    if (bufChars >= FLUSH_CHARS || ev.type === 2) flush(false);   // a full snapshot goes at once
    else schedule();
    if (Date.now() - T0 > MAX_PAGE_MS) stop();
  }
  function stop() {
    if (stopped) return;
    stopped = true;
    try { if (stopRecording) stopRecording(); } catch (e) { /* done */ }
    flush(true);
  }

  // ── recording ──
  function start() {
    CLIENT = clientId();
    if (!chosen(CLIENT)) return;
    var record = typeof window.rrwebRecord === 'function' ? window.rrwebRecord : window.rrwebRecord.record;
    try {
      stopRecording = record({
        emit: emit,
        maskAllInputs: true,
        maskInputFn: maskBox,
        maskTextSelector: MASK_TEXT,
        blockSelector: BLOCK,
        inlineStylesheet: false,
        inlineImages: false,
        collectFonts: false,
        recordCanvas: false,
        recordCrossOriginIframes: false,
        slimDOMOptions: { script: true, comment: true, headFavicon: true, headWhitespace: true, headMetaDescKeywords: true, headMetaSocial: true, headMetaRobots: true, headMetaHttpEquiv: true, headMetaAuthorship: true, headMetaVerification: true },
        sampling: { mousemove: 80, mouseInteraction: { MouseUp: false, MouseDown: false, Click: true, ContextMenu: false, DblClick: true, Focus: false, Blur: false, TouchStart: true, TouchMove_Departed: false, TouchEnd: false, TouchCancel: false }, scroll: 150, media: 800, input: 'last' },
      });
    } catch (e) { stopped = true; return; }
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(true); });
    window.addEventListener('pagehide', function () { flush(true); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
